import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';


import { learningCenterService } from '../../services/learningCenterService';

import ModuleCard from './components/ModuleCard';
import ProgressOverview from './components/ProgressOverview';
import SearchAndFilters from './components/SearchAndFilters';
import { Search, Filter } from 'lucide-react';

const IPMLearningCenter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [userStats, setUserStats] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    difficulty: '',
    isPremium: null,
    status: ''
  });
  const [activeView, setActiveView] = useState('pathway'); // pathway, modules, bookmarks

  useEffect(() => {
    loadLearningData();
  }, [user]);

  const loadLearningData = async () => {
    try {
      setLoading(true);
      setError('');

      if (user) {
        // Load authenticated user data
        const [modulesData, statsData, bookmarksData] = await Promise.all([
          learningCenterService?.getAllModulesWithProgress(user?.id),
          learningCenterService?.getUserStats(user?.id),
          learningCenterService?.getUserBookmarks(user?.id)
        ]);

        setModules(modulesData);
        setUserStats(statsData);
        setBookmarks(bookmarksData);
      } else {
        // Load demo data for non-authenticated users
        const demoModules = [
          {
            id: 'demo-1',
            title: 'IPM Fundamentals',
            description: 'Introduction to Integrated Pest Management principles and practices.',
            difficulty: 'beginner',
            estimated_duration: 45,
            is_premium: false,
            progress: null
          },
          {
            id: 'demo-2',
            title: 'Spider Mite Identification',
            description: 'Learn to identify different species of spider mites and their damage patterns.',
            difficulty: 'intermediate',
            estimated_duration: 30,
            is_premium: false,
            progress: null
          },
          {
            id: 'demo-3',
            title: 'Biological Control Strategies',
            description: 'Advanced biological control methods and beneficial insect selection.',
            difficulty: 'advanced',
            estimated_duration: 60,
            is_premium: true,
            progress: null
          },
          {
            id: 'demo-4',
            title: 'Treatment Timing Optimization',
            description: 'When and how to apply treatments for maximum effectiveness.',
            difficulty: 'intermediate',
            estimated_duration: 40,
            is_premium: true,
            progress: null
          }
        ];

        const demoStats = {
          modulesCompleted: 0,
          modulesInProgress: 0,
          certificatesEarned: 0,
          totalTimeSpent: 0
        };

        setModules(demoModules);
        setUserStats(demoStats);
        setBookmarks([]);
      }
    } catch (err) {
      console.error('Error loading learning data:', err);
      setError(err?.message || 'Failed to load learning center data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (query) => {
    try {
      setSearchQuery(query);
      if (user && query?.trim()) {
        const searchResults = await learningCenterService?.searchModules(query, filters);
        setModules(searchResults);
      } else if (user) {
        // Reload all modules if search is cleared for authenticated users
        const allModules = await learningCenterService?.getAllModulesWithProgress(user?.id);
        setModules(allModules);
      }
      // For non-authenticated users, implement basic client-side filtering
      else {
        loadLearningData(); // Reload demo data
      }
    } catch (err) {
      setError('Search failed: ' + err?.message);
    }
  };

  const handleFilterChange = async (newFilters) => {
    setFilters(newFilters);
    if (user) {
      try {
        if (searchQuery?.trim()) {
          const searchResults = await learningCenterService?.searchModules(searchQuery, newFilters);
          setModules(searchResults);
        } else {
          const allModules = await learningCenterService?.getAllModulesWithProgress(user?.id);
          // Apply client-side filtering
          let filteredModules = allModules;
          
          if (newFilters?.difficulty) {
            filteredModules = filteredModules?.filter(m => m?.difficulty === newFilters?.difficulty);
          }
          
          if (newFilters?.isPremium !== null) {
            filteredModules = filteredModules?.filter(m => m?.isPremium === newFilters?.isPremium);
          }
          
          if (newFilters?.status) {
            filteredModules = filteredModules?.filter(m => 
              m?.progress?.status === newFilters?.status || 
              (!m?.progress && newFilters?.status === 'not_started')
            );
          }
          
          setModules(filteredModules);
        }
      } catch (err) {
        setError('Filter failed: ' + err?.message);
      }
    }
  };

  const handleModuleProgress = async (moduleId, progressData) => {
    if (!user) {
      // Redirect to login for non-authenticated users
      navigate('/login');
      return;
    }

    try {
      await learningCenterService?.updateUserProgress(user?.id, moduleId, progressData);
      // Refresh data
      await loadLearningData();
    } catch (err) {
      setError('Failed to update progress: ' + err?.message);
    }
  };

  const handleBookmarkToggle = async (moduleId) => {
    if (!user) {
      navigate('/login');
      return;
    }

    try {
      await learningCenterService?.toggleBookmark(moduleId);
      // Refresh bookmarks
      const updatedBookmarks = await learningCenterService?.getUserBookmarks(user?.id);
      setBookmarks(updatedBookmarks);
    } catch (err) {
      setError('Failed to update bookmark: ' + err?.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900">IPM Learning Center</h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Educational resources and training modules</p>
        </div>

        {/* Search and Filters - Responsive */}
        <div className="mb-6">
          <SearchAndFilters 
            onSearch={handleSearch}
            onFilterChange={handleFilterChange}
            filters={filters}
            searchQuery={searchQuery}
          />
        </div>

        {/* Progress Overview - Responsive */}
        <div className="mb-6">
          {userStats && user && (
            <ProgressOverview 
              userStats={userStats}
              modules={modules}
            />
          )}
        </div>

        {/* Module Grid - Responsive columns */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {modules?.map(module => (
            <ModuleCard
              key={module.id}
              module={module}
              onProgressUpdate={handleModuleProgress}
              onBookmarkToggle={handleBookmarkToggle}
              isBookmarked={bookmarks?.some(b => b?.module?.id === module.id)}
              user={user}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default IPMLearningCenter;